<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import {
    UiModalHeader,
    FormInput,
    ColoredButton,
    ThemeProvider,
    CustomCheckbox,
    SimpleLoading,
  } from '@/shared/ui';
  import { positionApi, type Position } from '@/entities/Position';
  import type { Department } from '@/entities/Department';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import { fetchAllPages } from '@/shared/lib';

  const props = defineProps<{
    title?: string;
    department: Department | null;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'save', data: { addPositions: number[]; removePositions: number[]; code?: string }): void;
  }>();

  const isLoading = ref(false);
  const allPositions = ref<Position[]>([]);
  const selectedIds = ref<Set<number>>(new Set());
  const initialIds = ref<Set<number>>(new Set());
  const departmentCode = ref('');
  const initialCode = ref('');

  const hasChanges = computed(() => {
    if (departmentCode.value !== initialCode.value) return true;
    if (selectedIds.value.size !== initialIds.value.size) return true;
    for (const id of selectedIds.value) {
      if (!initialIds.value.has(id)) return true;
    }
    return false;
  });

  const isCodeValid = computed(() => departmentCode.value.trim().length > 0);
  const isFormValid = computed(() => isCodeValid.value);
  const showValidation = computed(() => hasChanges.value);

  const selectedCount = computed(() => selectedIds.value.size);

  const loadPositions = async () => {
    if (!props.department) return;

    isLoading.value = true;
    try {
      const positions = await fetchAllPages((page) =>
        positionApi.getAllPositions({ page, itemsPerPage: 1000 })
      );
      allPositions.value = positions;

      const departmentPositions = positions
        .filter((p) => p.departmentId === props.department?.id)
        .map((p) => p.id);

      departmentCode.value = props.department?.code || '';
      initialCode.value = props.department?.code || '';

      selectedIds.value = new Set(departmentPositions);
      initialIds.value = new Set(departmentPositions);
    } finally {
      isLoading.value = false;
    }
  };

  const togglePosition = (positionId: number) => {
    const newSet = new Set(selectedIds.value);
    if (newSet.has(positionId)) {
      newSet.delete(positionId);
    } else {
      newSet.add(positionId);
    }
    selectedIds.value = newSet;
  };

  const handleClose = () => {
    emit('close');
  };

  const handleSave = () => {
    if (!isFormValid.value) return;
    const added = [...selectedIds.value].filter((id) => !initialIds.value.has(id));
    const removed = [...initialIds.value].filter((id) => !selectedIds.value.has(id));
    const codeChanged = departmentCode.value !== initialCode.value;
    emit('save', {
      addPositions: added,
      removePositions: removed,
      code: codeChanged ? departmentCode.value : undefined,
    });
  };

  const handleClosed = () => {
    allPositions.value = [];
    selectedIds.value = new Set();
    initialIds.value = new Set();
    departmentCode.value = '';
    initialCode.value = '';
  };

  watch(
    () => props.department,
    (newDepartment) => {
      if (newDepartment) {
        departmentCode.value = newDepartment.code || '';
        initialCode.value = newDepartment.code || '';
        loadPositions();
      }
    },
    { immediate: true }
  );

  defineExpose({ togglePosition, loadPositions, handleSave, hasChanges });
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[1000px] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @closed="handleClosed"
  >
    <ThemeProvider class="flex flex-col">
      <div class="flex flex-col">
        <UiModalHeader
          class="px-6 py-4 text-sm border-b border-gray-02 shrink-0"
          :title="title || 'Editar Departamento'"
          @close="handleClose"
        />

        <div class="px-6 py-6 space-y-6">
          <FormInput
            id="department-code"
            v-model="departmentCode"
            label="Departamento:"
            type="text"
            placeholder=""
            label-class="text-sm text-gray-07 font-semibold"
            :error="
              !isCodeValid && showValidation ? 'Informe o código do departamento.' : undefined
            "
          />

          <hr class="border-t border-gray-02 -mx-6">

          <div class="flex flex-col gap-2">
            <div class="flex flex-col mb-2">
              <label class="text-sm text-gray-07 font-semibold">Cargos:</label>
              <span class="text-xs text-gray-05">
                Selecione os cargos que pertencem ao Departamento
              </span>
            </div>

            <div
              v-if="isLoading"
              class="flex justify-center py-8"
            >
              <SimpleLoading />
            </div>

            <div
              v-else
              class="border rounded-lg p-4 h-96 overflow-y-auto"
            >
              <div
                v-for="position in allPositions"
                :key="position.id"
                class="flex items-center py-2 hover:bg-gray-50 rounded px-2 cursor-pointer"
                @click="togglePosition(position.id)"
              >
                <CustomCheckbox
                  :id="`position-${position.id}`"
                  :checked="selectedIds.has(position.id)"
                />
                <span class="ml-3 text-sm text-gray-07 select-none">
                  {{ position.name }}
                </span>
              </div>

              <div
                v-if="allPositions.length === 0"
                class="flex justify-center items-center h-full text-gray-05"
              >
                Nenhum cargo disponível
              </div>
            </div>

            <span class="text-xs text-gray-05">
              Total de cargos selecionados: {{ selectedCount }}
            </span>
          </div>
        </div>

        <div class="px-6 py-8 flex justify-end gap-3 border-t border-gray-02 shrink-0">
          <ColoredButton
            text="Cancelar"
            outlined
            color="gray-03"
            @click="handleClose"
          />
          <ColoredButton
            :disabled="!hasChanges || !isFormValid"
            text="Salvar"
            :icon="CheckMarkIcon"
            @click="handleSave"
          />
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>

<style scoped>
  .vfm__overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
